import { ApiMessageStack, Error, Loader, TableV2 } from "components";
import { observer } from "mobx-react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { UserAccountStore } from "store/UserAccounts/UserAccount/UserAccount.store";
import { ApiRequestStatusEnum } from "enums/api-request-status.enum";
import { Box, Button, Text, useToast, VStack } from "@chakra-ui/react";
import { useActionPrompt } from "utils/react-hooks/useActionPrompt.hook";
import { ConfirmActionPrompt } from "routes/dashboard/components/prompts/ConfirmActionPrompt";
import {
  DEFAULT_ERROR_TOAST_OPTIONS,
  DEFAULT_SUCCESS_TOAST_OPTIONS,
} from "constants/default-toast-options";
import {
  TAccountLeadInactivity,
  TAccountLeadInactivityPayload,
} from "types/account-lead-inactivity.type";
import { getLeadTypeLabel } from "utils/lead-type.utils";
import { UNASSIGNED_SOURCE_OFFICE } from "constants/lead-inactivity";
import { leadInactivityColumnDef } from "./leadInactivityColumnDef";
import { LeadInactivityRuleActionPrompt } from "./ActionPrompts/LeadInactivityRuleActionPrompt";

const ALL_LEAD_TYPES_LABEL = "all lead types";
const ALL_SOURCE_OFFICES_LABEL = "all offices";
const UNASSIGNED_SOURCE_OFFICE_LABEL = "Unassigned";
const MISSING_OFFICE_LABEL = "an office that no longer exists";

const joinList = (items: string[]): string =>
  items.length > 1
    ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`
    : items[0];

const describeOffices = (
  officeIds: number[],
  getOfficeName: (officeId: number) => string,
  isOfficeMissing: (officeId: number) => boolean
): string[] => {
  const names = officeIds
    .filter(officeId => !isOfficeMissing(officeId))
    .map(officeId => getOfficeName(officeId));
  const missingCount = officeIds.length - names.length;

  if (!missingCount) return names;

  return [
    ...names,
    missingCount === 1
      ? MISSING_OFFICE_LABEL
      : `${missingCount} offices that no longer exist`,
  ];
};

const describeRule = (
  rule: TAccountLeadInactivity,
  getOfficeName: (officeId: number) => string,
  isOfficeMissing: (officeId: number) => boolean
): string => {
  const leadTypes = rule.leadTypes?.length
    ? joinList(rule.leadTypes.map(leadType => getLeadTypeLabel(leadType, true)))
    : ALL_LEAD_TYPES_LABEL;

  const sourceOffices = rule.sourceOfficeIds?.length
    ? joinList(
        describeOffices(rule.sourceOfficeIds, getOfficeName, isOfficeMissing)
      )
    : ALL_SOURCE_OFFICES_LABEL;

  const destination = isOfficeMissing(rule.config.officeId)
    ? MISSING_OFFICE_LABEL
    : getOfficeName(rule.config.officeId);

  return `${leadTypes} from ${sourceOffices} redirecting to ${destination}`;
};

interface IProps {
  accountStore: UserAccountStore;
}

export const LeadInactivity: FC<IProps> = observer(({ accountStore }) => {
  const [loadingStatus, setLoadingStatus] = useState(ApiRequestStatusEnum.NONE);
  const toast = useToast();
  const { setModal, unSetModal } = useActionPrompt();

  const {
    fetchLeadInactivity,
    leadInactivityRulesArray,
    createLeadInactivity,
    updateLeadInactivity,
    deleteLeadInactivity,
  } = accountStore.accountLeadInactivityStore;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingStatus(ApiRequestStatusEnum.PENDING);
        await fetchLeadInactivity();
        setLoadingStatus(ApiRequestStatusEnum.SUCCESS);
      } catch (error) {
        setLoadingStatus(ApiRequestStatusEnum.ERROR);
      }
    };

    fetchData();
  }, [setLoadingStatus, fetchLeadInactivity]);

  const accountOfficesStore = accountStore.accountOfficesStore;
  const officeOptions = accountOfficesStore.accountOfficesOptionsArray;
  const officesMap = accountOfficesStore.accountOfficesDataMap;

  const getOfficeName = useCallback(
    (officeId: number) =>
      officeId === UNASSIGNED_SOURCE_OFFICE
        ? UNASSIGNED_SOURCE_OFFICE_LABEL
        : officesMap[officeId]?.name || `Office #${officeId}`,
    [officesMap]
  );

  const isOfficeMissing = useCallback(
    (officeId: number) =>
      officeId !== UNASSIGNED_SOURCE_OFFICE && !officesMap[officeId],
    [officesMap]
  );

  const onDeleteRule = useCallback(
    (leadInactivityId: number) => async () => {
      try {
        await deleteLeadInactivity(leadInactivityId);
        toast({
          ...DEFAULT_SUCCESS_TOAST_OPTIONS,
          description: (
            <ApiMessageStack messageStack={"Redirect rule removed."} />
          ),
        });
      } catch (e) {
        toast({
          ...DEFAULT_ERROR_TOAST_OPTIONS,
          description: <ApiMessageStack messageStack={e.message} />,
        });
      }
    },
    [deleteLeadInactivity, toast]
  );

  const handleRemove = useCallback(
    (row: TAccountLeadInactivity) => () => {
      setModal(
        <ConfirmActionPrompt
          text={`Please confirm that you want to remove the "${describeRule(
            row,
            getOfficeName,
            isOfficeMissing
          )}" redirect rule.`}
          onConfirm={onDeleteRule(row.id)}
          closePrompt={unSetModal}
        />
      );
    },
    [onDeleteRule, setModal, unSetModal, getOfficeName, isOfficeMissing]
  );

  const handleEdit = useCallback(
    (row: TAccountLeadInactivity) => () => {
      setModal(
        <LeadInactivityRuleActionPrompt
          closePrompt={unSetModal}
          officeOptions={officeOptions}
          getOfficeName={getOfficeName}
          rule={row}
          onSave={(payload: TAccountLeadInactivityPayload) =>
            updateLeadInactivity(row.id, payload)
          }
        />
      );
    },
    [setModal, unSetModal, officeOptions, getOfficeName, updateLeadInactivity]
  );

  const handleAdd = useCallback(() => {
    setModal(
      <LeadInactivityRuleActionPrompt
        closePrompt={unSetModal}
        officeOptions={officeOptions}
        getOfficeName={getOfficeName}
        onSave={createLeadInactivity}
      />
    );
  }, [
    setModal,
    unSetModal,
    officeOptions,
    getOfficeName,
    createLeadInactivity,
  ]);

  const columns = useMemo(
    () =>
      leadInactivityColumnDef(
        getOfficeName,
        isOfficeMissing,
        handleEdit,
        handleRemove
      ),
    [getOfficeName, isOfficeMissing, handleEdit, handleRemove]
  );

  return (
    <VStack spacing={8} align={"stretch"}>
      <Text fontSize={"2xl"} fontWeight={"bold"} mb={-4}>
        Lead redirect rules
      </Text>
      <Text fontSize={"sm"} color={"leadpro.500"}>
        Redirect leads that are inactive. Add a rule for each lead type that
        needs its own waiting time or destination office.
      </Text>
      {loadingStatus === ApiRequestStatusEnum.PENDING && <Loader />}
      {loadingStatus === ApiRequestStatusEnum.ERROR && (
        <Box position={"relative"} minHeight={"200px"}>
          <Error />
        </Box>
      )}
      {loadingStatus === ApiRequestStatusEnum.SUCCESS && (
        <TableV2<TAccountLeadInactivity>
          striped
          dataSource={leadInactivityRulesArray}
          columns={columns}
          additionalActions={
            <Box flexGrow={1}>
              <Button variant={"link"} colorScheme={"blue"} onClick={handleAdd}>
                + Add redirect rule
              </Button>
            </Box>
          }
        />
      )}
    </VStack>
  );
});
